// /opt/ptg01-v1/front-extranet/components/IdleTimeout.tsx
'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Clock } from 'lucide-react';
import { fetchExtranet } from '@/lib/fetchExtranet';
import { useAuthOptional } from '@/lib/AuthContext';

const IDLE_LIMIT = 15 * 60 * 1000;   // 15 minutes
const WARNING_BEFORE = 60 * 1000;     // modal 1 minute avant

const EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

export default function IdleTimeout() {
    const router = useRouter();
    const auth = useAuthOptional();
    // Initialisée au montage, pas pendant le rendu : Date.now() en rendu rend
    // celui-ci impur (react-hooks/purity).
    const lastActivity = useRef<number | null>(null);
    const [showWarning, setShowWarning] = useState(false);
    const [remaining, setRemaining] = useState(60);

    const resetTimer = useCallback(() => {
        lastActivity.current = Date.now();
        setShowWarning(false);
    }, []);

    // Track user activity
    useEffect(() => {
        lastActivity.current = Date.now();
        const handler = () => { lastActivity.current = Date.now(); };
        EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }));
        return () => { EVENTS.forEach(e => window.removeEventListener(e, handler)); };
    }, []);

    // Check idle every second
    useEffect(() => {
        const interval = setInterval(() => {
            const idle = Date.now() - (lastActivity.current ?? Date.now());
            const timeLeft = IDLE_LIMIT - idle;

            if (timeLeft <= 0) {
                // Déconnexion : serveur (invalide sessionId + clear cookie) + client (context + localStorage)
                clearInterval(interval);
                fetchExtranet('/api/auth/logout', { method: 'POST' }).catch(() => {});
                auth?.setUser(null);
                router.push('/auth/login');
                return;
            }

            if (timeLeft <= WARNING_BEFORE) {
                setShowWarning(true);
                setRemaining(Math.ceil(timeLeft / 1000));
            } else {
                setShowWarning(false);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [router, auth]);

    if (!showWarning) return null;

    return (
        <div className="fixed inset-0 bg-slate-900/50 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-[#000091]/10 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-[#000091]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Session bientôt expirée</h3>
                <p className="text-sm text-slate-500 mb-1">
                    Vous serez déconnecté dans
                </p>
                <p className="text-3xl font-bold text-[#000091] mb-4">
                    {remaining}<span className="text-sm font-semibold text-slate-400 ml-1">s</span>
                </p>
                <p className="text-xs text-slate-400 mb-5">
                    Pour des raisons de sécurité, votre session expire après 15 minutes d&apos;inactivité.
                </p>
                <button
                    onClick={resetTimer}
                    className="w-full h-11 bg-[#000091] text-white rounded-lg text-sm font-bold hover:bg-[#1e40af] transition-colors flex items-center justify-center gap-2"
                >
                    Rester connecté
                </button>
            </div>
        </div>
    );
}
